import React, { useState } from 'react';
import { UserPermissionLabels } from '../../../types/UserPermission';
import type { MasterSearchFilters } from '../../../types/masterSearchTypes';

/** * ユーザー管理画面専用の検索条件インターフェース
 * 共通の MasterSearchFilters を継承し、権限（permission）を追加
 */
export interface UserSearchFilters extends MasterSearchFilters {
  /** 権限 */
  permission?: number;
  /** ロック状態のみ表示 */
  onlyLocked?: boolean;
}

/**
 * プロパティの型定義
 */
interface Props {
  onSearch: (filters: UserSearchFilters) => void; // 検索実行時のコールバック関数
  initialFilters: UserSearchFilters;              // コンポーネント初期化時のフィルター状態
}

/**
 * ユーザー一覧用 検索フォームコンポーネント
 * ログインID、氏名、権限、削除済データの有無に基づいたフィルタリング条件を入力します。
 */
export const UserMaintenanceSearchForm = ({ onSearch, initialFilters }: Props) => {
  // --- 1. States：入力フォームの値を管理 ---
  
  const [code, setCode] = useState(initialFilters.code || ''); // ログインID
  const [name, setName] = useState(initialFilters.name || ''); // 氏名
  const [permission, setPermission] = useState<number | undefined>(initialFilters.permission);  // 権限
  const [includeDeleted, setIncludeDeleted] = useState(initialFilters.includeDeleted || false); // 無効化済みユーザーを含めるかのフラグ
  const [onlyLocked, setOnlyLocked] = useState(initialFilters.onlyLocked || false); // ロック済みのみ表示するかのフラグ

  // --- 2. Handlers：イベント処理 ---
  
  /** * 検索実行（フォーム送信）時の処理 
   * @param e フォームイベント
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({ code, name, permission, includeDeleted, onlyLocked });
  };

  /** * クリアボタンクリック時の処理
   * 全ての検索条件を初期化し、即座に全件検索を実行します。
   */
  const handleClear = () => {
    setCode('');
    setName('');
    setPermission(undefined);
    setIncludeDeleted(false);
    setOnlyLocked(false);
    // 初期化状態を渡して再検索を実行
    onSearch({ code: '', name: '', permission: undefined, includeDeleted: false, onlyLocked: false });
  };

  /** * 削除済フラグ切り替え時の処理
   * チェック状態の変更後、即座に最新の値を反映して検索を実行します。
   * @param e チェンジイベント
   */
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.checked;
    setIncludeDeleted(newVal);
    // Stateの更新（非同期）を待たずに最新の値を渡して即座に検索
    onSearch({ code, name, permission, includeDeleted: newVal, onlyLocked });
  };

  // ロック状態のみ切り替え時の処理
  const handleLockSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 1. このイベントで確定した最新の値をローカル変数に保持
    const nextOnlyLocked = e.target.checked;
    // 2. 「ロック状態のみ」がONになった場合、「無効化済を含める」は強制的にOFFにする
    const nextIncludeDeleted = nextOnlyLocked ? false : includeDeleted;

    // 3. Stateを更新（画面表示用）
    setOnlyLocked(nextOnlyLocked);
    setIncludeDeleted(nextIncludeDeleted);

    onSearch({ code, name, permission, includeDeleted: nextIncludeDeleted, onlyLocked: nextOnlyLocked });
  };

  /** * 権限選択変更時の処理
   * 選択変更後、即座に検索を実行します。
   * @param e セレクトチェンジイベント
   */
  const handlePermissionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value === '' ? undefined : Number(e.target.value);
    setPermission(val);
    onSearch({ code, name, permission: val, includeDeleted, onlyLocked });
  };

  // --- 3. メインレンダリング ---
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex-shrink-0">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-4">
        
        {/* ログインID 入力エリア */}
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600 font-bold">ログインID</label>
          <input 
            type="text" 
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-36 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm h-10"
            placeholder="完全/前方一致"
          />
        </div>

        {/* 氏名 (あいまい) 入力エリア */}
        <div className="flex flex-col gap-1 flex-grow">
          <label className="text-sm text-gray-600 font-bold">氏名 (あいまい)</label>
          <input 
            type="text" 
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm h-10"
            placeholder="氏名を入力..."
          />
        </div>

        {/* 権限 絞り込みセレクトボックス */}
        <div className="flex flex-col gap-1 w-48">
          <label className="text-sm text-gray-600 font-bold">権限</label>
          <select
            value={permission ?? ''}
            onChange={handlePermissionChange}
            className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm h-10 cursor-pointer bg-white"
          >
            <option value="">すべて</option>
            {/* UserPermissionLabels に基づいて選択肢を動的に生成 */}
            {Object.entries(UserPermissionLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* 削除済アカウントの表示切り替え（トグルスイッチ形式） */}
        <div className="flex flex-col gap-1 h-[68px]">
        <label className="text-sm font-medium text-gray-700 select-none whitespace-nowrap">無効化済を含める</label>
        <div className="flex items-center h-10">
           <label className="relative inline-flex items-center cursor-pointer">
             <input 
               type="checkbox" 
               className="sr-only peer"
               checked={includeDeleted}
               onChange={handleSwitchChange} 
             />
             {/* w-15=60px スイッチ全体の横幅
                 after:w-5=20px 動く円（スライダー）自体の横幅
                 after:left-[2px] =4px 左右の隙間の合計
                 60-20-4=36
             */}
             <div className="w-15 h-6 bg-gray-200 rounded-full peer 
              peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 
              peer-checked:bg-blue-600 
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
              after:bg-white after:border-gray-300 after:border after:rounded-full 
              after:h-5 after:w-5 after:transition-all 
              peer-checked:after:translate-x-[36px]">
            </div>
           </label>
           </div>
        </div>

        {/* ロック状態のみ表示（トグルスイッチ形式） */}
        <div className="flex flex-col gap-1 h-[68px]">
        <label className="text-sm font-medium text-gray-700 select-none whitespace-nowrap">ロック状態のみ</label>
        <div className="flex items-center h-10">
           <label className="relative inline-flex items-center cursor-pointer">
             <input 
               type="checkbox" 
               className="sr-only peer"
               checked={onlyLocked}
               onChange={handleLockSwitchChange} 
             />
             {/* w-15=60px スイッチ全体の横幅
                 after:w-5=20px 動く円（スライダー）自体の横幅
                 after:left-[2px] =4px 左右の隙間の合計
                 60-20-4=36
             */}
             <div className="w-15 h-6 bg-gray-200 rounded-full peer 
              peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 
              peer-checked:bg-blue-600 
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
              after:bg-white after:border-gray-300 after:border after:rounded-full 
              after:h-5 after:w-5 after:transition-all 
              peer-checked:after:translate-x-[36px]">
            </div>
           </label>
           </div>
        </div>

        {/* フォームアクションボタン群 */}
        <div className="flex gap-2 pb-0.5 ml-auto">
          <button 
            type="submit" 
            className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 transition-colors font-medium shadow-sm h-10"
          >
            検索
          </button>
          <button 
            type="button" 
            onClick={handleClear}
            className="text-gray-600 px-4 py-2 hover:bg-gray-100 rounded transition-colors border border-gray-300 bg-white font-medium h-10"
          >
            クリア
          </button>
        </div>
      </form>
    </div>
  );
};